import React from 'react';
import {
    Alert, AlertTitle, Avatar, Box, Card, CardActionArea, CardContent, Chip, Grid, IconButton, LinearProgress, List,
    ListItemButton, ListItemText, MenuItem, Paper, Select, Tooltip, Typography, useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArticleIcon from '@mui/icons-material/Article';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import QuizIcon from '@mui/icons-material/Quiz';
import GavelIcon from '@mui/icons-material/Gavel';
import DescriptionIcon from '@mui/icons-material/Description';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import HandshakeIcon from '@mui/icons-material/Handshake';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ApartmentIcon from '@mui/icons-material/Apartment';
import EventIcon from '@mui/icons-material/Event';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import PageHeader from '../../../components/page-header';
import { RowAction } from '../../../components/row-action';
import { EmptyState } from '../../../components/empty-state';
import { useDashboardController } from './context/dashboard-controller';
import { useDashboardState } from './context/dashboard-context';
import { STATUS_LICITACAO_LABEL, StatusLicitacao } from '../../../model/apm/apm-enums';
import { formatCompactNumber, formatCurrency } from '../../../utils/format';
import { BIDDING_STATUS_COLOR } from '../bidding/components/bidding-status';
import { ALERT_ROUTES, ALERT_SEVERITY, UPCOMING_DAYS_OPTIONS, daysUntil, daysUntilLabel, percentOf } from './components/dashboard-utils';
import { formatDate, formatDateTime } from '../../../utils/date';

type PaletteKey = 'primary' | 'success' | 'warning' | 'error' | 'info' | 'secondary';


function SectionTitle({ title, subtitle }: { title: string; subtitle?: string }) {
    return (
        <Grid item xs={12}>
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>{title}</Typography>
            {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
        </Grid>
    );
}

// Card de indicador clicável: valor principal, detalhes e, opcionalmente, barra de proporção
function KpiCard({ label, value, icon, color = 'primary', details, progress, onClick }: {
    label: string;
    value: React.ReactNode;
    icon: React.ReactNode;
    color?: PaletteKey;
    details?: React.ReactNode[];
    progress?: { value: number; label: string };
    onClick?: () => void;
}) {
    return (
        <Card sx={{ height: '100%', transition: 'all 0.3s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 } }}>
            <CardActionArea onClick={onClick} disabled={!onClick} sx={{ height: '100%', alignItems: 'stretch' }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography color="text.secondary" variant="overline" noWrap component="div">{label}</Typography>
                            <Typography color="text.primary" variant="h4" fontWeight={600}>{value}</Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: `${color}.main`, width: 48, height: 48 }}>{icon}</Avatar>
                    </Box>
                    {progress && (
                        <Box sx={{ mt: 1.5 }}>
                            <LinearProgress variant="determinate" value={progress.value} color={color === 'secondary' ? 'primary' : color} sx={{ height: 6, borderRadius: 3 }} />
                            <Typography variant="caption" color="text.secondary">{progress.label}</Typography>
                        </Box>
                    )}
                    {details && details.filter(Boolean).length > 0 && (
                        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                            {details.filter(Boolean).map((d, i) => (
                                <Typography key={i} variant="caption" color="text.secondary" component="div">{d}</Typography>
                            ))}
                        </Box>
                    )}
                </CardContent>
            </CardActionArea>
        </Card>
    );
}

function ListPanel({ title, icon, actionLabel, onAction, emptyMessage, children, isEmpty }: {
    title: string; icon: React.ReactNode; actionLabel: string; onAction: () => void; emptyMessage: string; children: React.ReactNode; isEmpty: boolean;
}) {
    return (
        <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
                <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>{title}</Typography>
                <RowAction title={actionLabel} onClick={onAction}><ChevronRightIcon fontSize="small" /></RowAction>
            </Box>
            {isEmpty ? (
                <EmptyState icon={icon} message={emptyMessage} />
            ) : (
                <List dense disablePadding>{children}</List>
            )}
        </Paper>
    );
}

export function DashboardComponent() {
    const theme = useTheme();
    const { loadData, handleUpcomingDaysChange, navigate } = useDashboardController();
    const { data, loading, upcomingDays } = useDashboardState();
    const content = data?.content;
    const transparency = data?.transparency;
    const reference = data?.referenceDate || new Date().toISOString();
    const alerts = (data?.alerts || []).filter((a) => a.count > 0);

    const statusData = (data?.biddingsByStatus || [])
        .filter((s) => s.count > 0)
        .map((s) => {
            const colorKey = BIDDING_STATUS_COLOR[s.status as StatusLicitacao];
            const color = colorKey && colorKey !== 'default' ? theme.palette[colorKey].main : theme.palette.grey[500];
            return { name: STATUS_LICITACAO_LABEL[s.status as StatusLicitacao] || `Situação ${s.status}`, count: s.count, color };
        })
        .sort((a, b) => b.count - a.count);

    const headerActions = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>Prazos dos próximos</Typography>
            <Select size="small" value={upcomingDays} onChange={(e) => handleUpcomingDaysChange(Number(e.target.value))}>
                {UPCOMING_DAYS_OPTIONS.map((d) => <MenuItem key={d} value={d}>{d} dias</MenuItem>)}
            </Select>
            <Tooltip title="Atualizar">
                <span>
                    <IconButton onClick={loadData} disabled={loading}><RefreshIcon /></IconButton>
                </span>
            </Tooltip>
        </Box>
    );

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title="Dashboard"
                    subtitle={data ? `Visão geral do portal da entidade · atualizado em ${formatDateTime(data.referenceDate) || '-'}` : 'Visão geral do portal da entidade'}
                    breadcrumbs={[{ label: 'Dashboard' }]}
                    actions={headerActions}
                />
            </Grid>

            {loading && (
                <Grid item xs={12}>
                    <LinearProgress />
                </Grid>
            )}

            {/* Pendências */}
            {data && (
                <Grid item xs={12}>
                    {alerts.length === 0 ? (
                        <Alert severity="success" icon={<CheckCircleOutlineIcon />}>Nenhuma pendência no portal. Tudo em dia!</Alert>
                    ) : (
                        <Paper sx={{ p: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>Pendências e avisos</Typography>
                            <Grid container spacing={1.5}>
                                {alerts.map((a) => (
                                    <Grid item xs={12} sm={6} lg={4} key={a.key}>
                                        <Alert
                                            severity={ALERT_SEVERITY[a.key || ''] || 'info'}
                                            action={a.key && ALERT_ROUTES[a.key] ? (
                                                <IconButton size="small" color="inherit" onClick={() => navigate(ALERT_ROUTES[a.key!])}>
                                                    <ChevronRightIcon fontSize="small" />
                                                </IconButton>
                                            ) : undefined}
                                            sx={{ height: '100%', alignItems: 'center' }}
                                        >
                                            <AlertTitle sx={{ mb: 0 }}>{a.count}</AlertTitle>
                                            {a.message}
                                        </Alert>
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>
                    )}
                </Grid>
            )}

            {/* Conteúdo do portal */}
            <SectionTitle title="Conteúdo do portal" subtitle="Páginas, notícias e banners publicados" />
            <Grid item xs={12} sm={6} lg={3}>
                <KpiCard
                    label="Páginas"
                    value={content?.pages ?? 0}
                    icon={<ArticleIcon />}
                    progress={content ? { value: percentOf(content.publishedPages, content.pages), label: `${content.publishedPages} publicada(s)` } : undefined}
                    onClick={() => navigate('/paginas')}
                />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
                <KpiCard
                    label="Notícias"
                    value={content?.news ?? 0}
                    icon={<NewspaperIcon />}
                    color="info"
                    progress={content ? { value: percentOf(content.publishedNews, content.news), label: `${content.publishedNews} publicada(s)` } : undefined}
                    details={content ? [
                        content.scheduledNews > 0 ? `${content.scheduledNews} agendada(s)` : '',
                        `${formatCompactNumber(content.newsViews)} visualização(ões)`,
                    ] : undefined}
                    onClick={() => navigate('/noticias')}
                />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
                <KpiCard
                    label="Banners"
                    value={content?.banners ?? 0}
                    icon={<ViewCarouselIcon />}
                    color="secondary"
                    progress={content ? { value: percentOf(content.activeBanners, content.banners), label: `${content.activeBanners} ativo(s)` } : undefined}
                    details={content && content.expiredBanners > 0 ? [`${content.expiredBanners} com período encerrado`] : undefined}
                    onClick={() => navigate('/banners')}
                />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
                <KpiCard
                    label="Perguntas e Respostas"
                    value={content?.questionAnswers ?? 0}
                    icon={<QuizIcon />}
                    color="success"
                    onClick={() => navigate('/perguntas-respostas')}
                />
            </Grid>

            {/* Transparência */}
            <SectionTitle title="Transparência" subtitle={`Licitações, contratos e publicações · prazos dos próximos ${data?.upcomingDays ?? upcomingDays} dias`} />
            <Grid item xs={12} sm={6} lg={3}>
                <KpiCard
                    label="Licitações"
                    value={transparency?.biddings ?? 0}
                    icon={<GavelIcon />}
                    details={transparency ? [
                        `${transparency.biddingsInProgress} em andamento`,
                        `${transparency.upcomingOpenings} abertura(s) próxima(s)`,
                        transparency.biddingsWithoutFiles > 0 ? `${transparency.biddingsWithoutFiles} sem arquivos` : '',
                    ] : undefined}
                    onClick={() => navigate('/licitacoes')}
                />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
                <KpiCard
                    label="Contratos"
                    value={transparency?.purchaseContracts ?? 0}
                    icon={<DescriptionIcon />}
                    color={transparency && transparency.contractsExpiringSoon > 0 ? 'warning' : 'primary'}
                    details={transparency ? [
                        `${transparency.contractsExpiringSoon} a vencer · ${transparency.expiredContracts} vencido(s)`,
                        `Pago ${formatCurrency(transparency.contractsAmountPaid)}`,
                        `Saldo ${formatCurrency(transparency.contractsBalance)}`,
                    ] : undefined}
                    onClick={() => navigate('/contratos')}
                />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
                <KpiCard
                    label="Contas Públicas"
                    value={transparency?.publicAccounts ?? 0}
                    icon={<AccountBalanceIcon />}
                    color="info"
                    details={transparency ? [
                        `${transparency.publicAccountsThisYear} publicada(s) neste ano`,
                        `Última em ${formatDate(transparency.lastPublicAccountDate) || '-'}`,
                    ] : undefined}
                    onClick={() => navigate('/contas-publicas')}
                />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
                <KpiCard
                    label="Convênios e Repasses"
                    value={transparency?.agreements ?? 0}
                    icon={<HandshakeIcon />}
                    color="success"
                    details={transparency ? [`Repassado ${formatCurrency(transparency.agreementsTransferAmount)}`] : undefined}
                    onClick={() => navigate('/convenios-repasses')}
                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <KpiCard label="Auditorias" value={transparency?.audits ?? 0} icon={<FactCheckIcon />} color="secondary" onClick={() => navigate('/auditorias')} />
            </Grid>
            <Grid item xs={12} sm={4}>
                <KpiCard label="Projetos de Governo" value={transparency?.governmentProjects ?? 0} icon={<AccountTreeIcon />} color="secondary" onClick={() => navigate('/projetos-governo')} />
            </Grid>
            <Grid item xs={12} sm={4}>
                <KpiCard label="Unidades Gestoras ativas" value={transparency?.activeManagementUnits ?? 0} icon={<ApartmentIcon />} color="secondary" onClick={() => navigate('/unidades-gestoras')} />
            </Grid>

            {/* Acompanhamento */}
            <SectionTitle title="Acompanhamento" />
            <Grid item xs={12} lg={5}>
                <Paper sx={{ p: 2, height: '100%' }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Licitações por situação</Typography>
                    {statusData.length === 0 ? (
                        <EmptyState icon={<GavelIcon />} message="Nenhuma licitação cadastrada" />
                    ) : (
                        <ResponsiveContainer width="100%" height={Math.max(180, statusData.length * 40)}>
                            <BarChart data={statusData} layout="vertical" margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                <XAxis type="number" allowDecimals={false} />
                                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 12 }} />
                                <ChartTooltip formatter={(value: any) => [value, 'Licitações']} />
                                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                                    {statusData.map((s) => <Cell key={s.name} fill={s.color} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </Paper>
            </Grid>
            <Grid item xs={12} md={6} lg={3.5}>
                <ListPanel
                    title="Próximas aberturas"
                    icon={<EventIcon />}
                    actionLabel="Ver licitações"
                    onAction={() => navigate('/licitacoes')}
                    emptyMessage={`Nenhuma abertura nos próximos ${data?.upcomingDays ?? upcomingDays} dias`}
                    isEmpty={!data?.upcomingBiddings?.length}
                >
                    {data?.upcomingBiddings?.map((b) => (
                        <ListItemButton key={b.id} onClick={() => navigate('/licitacoes')} sx={{ borderRadius: 1 }}>
                            <ListItemText
                                primary={b.number}
                                secondary={[b.modality, formatDateTime(b.openingDate) || '-'].filter(Boolean).join(' · ')}
                                primaryTypographyProps={{ fontWeight: 600, noWrap: true }}
                                secondaryTypographyProps={{ noWrap: true }}
                            />
                            <Chip size="small" color="info" variant="outlined" label={daysUntilLabel(daysUntil(b.openingDate, reference))} />
                        </ListItemButton>
                    ))}
                </ListPanel>
            </Grid>
            <Grid item xs={12} md={6} lg={3.5}>
                <ListPanel
                    title="Contratos a vencer"
                    icon={<DescriptionIcon />}
                    actionLabel="Ver contratos"
                    onAction={() => navigate('/contratos')}
                    emptyMessage={`Nenhum contrato vence nos próximos ${data?.upcomingDays ?? upcomingDays} dias`}
                    isEmpty={!data?.expiringContracts?.length}
                >
                    {data?.expiringContracts?.map((c) => {
                        const days = daysUntil(c.contractDeadline, reference);
                        return (
                            <ListItemButton key={c.id} onClick={() => navigate('/contratos')} sx={{ borderRadius: 1 }}>
                                <ListItemText
                                    primary={c.contractedCompanyName || c.processNumber}
                                    secondary={[c.processNumber ? `Processo ${c.processNumber}` : '', c.balance != null ? `Saldo ${formatCurrency(c.balance)}` : ''].filter(Boolean).join(' · ')}
                                    primaryTypographyProps={{ fontWeight: 600, noWrap: true }}
                                    secondaryTypographyProps={{ noWrap: true }}
                                />
                                <Chip size="small" color={days !== undefined && days <= 7 ? 'warning' : 'default'} variant="outlined" label={daysUntilLabel(days)} />
                            </ListItemButton>
                        );
                    })}
                </ListPanel>
            </Grid>
            <Grid item xs={12}>
                <ListPanel
                    title="Notícias recentes"
                    icon={<NewspaperIcon />}
                    actionLabel="Ver notícias"
                    onAction={() => navigate('/noticias')}
                    emptyMessage="Nenhuma notícia cadastrada"
                    isEmpty={!data?.recentNews?.length}
                >
                    {data?.recentNews?.map((n) => (
                        <ListItemButton key={n.id} onClick={() => navigate('/noticias')} sx={{ borderRadius: 1, gap: 1 }}>
                            <ListItemText
                                primary={n.title}
                                secondary={formatDate(n.date) || '-'}
                                primaryTypographyProps={{ fontWeight: 500, noWrap: true }}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', flexShrink: 0 }}>
                                <VisibilityIcon sx={{ fontSize: 16 }} />
                                <Typography variant="caption">{formatCompactNumber(n.views)}</Typography>
                            </Box>
                            <Chip size="small" color={n.isPublished ? 'success' : 'default'} label={n.isPublished ? 'Publicada' : 'Rascunho'} sx={{ flexShrink: 0 }} />
                        </ListItemButton>
                    ))}
                </ListPanel>
            </Grid>
        </React.Fragment>
    );
}
